import { api } from '@cma/app'
import {
  PlatformContentQuery,
  UpdatePlatformClientContentMutation,
  UpdatePlatformClientContentMutationVariables
} from '@cma/generated/graphql'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { PlatformClientContentFragment } from './usePlatformContent'

export function useMoveFiles({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient()

  return useMutation<
    UpdatePlatformClientContentMutation,
    Error,
    UpdatePlatformClientContentMutationVariables,
    { previousData?: PlatformContentQuery }
  >(
    (variables) => {
      const query = gql`
        mutation MoveFiles($ids: [ID!]!, $folderId: ID) {
          updatePlatformClientContent(ids: $ids, folderId: $folderId) {
            errors
            updatedIds
            platformClientContents {
              ...PlatformClientContentParts
            }
          }
        }

        ${PlatformClientContentFragment}
      `
      return api<
        UpdatePlatformClientContentMutation,
        UpdatePlatformClientContentMutationVariables
      >({ query, variables })
    },
    {
      onMutate: async ({ ids, folderId }) => {
        await queryClient.cancelQueries(['platformContent', { id: accountId }])

        const previousData = queryClient.getQueryData<PlatformContentQuery>([
          'platformContent',
          { id: accountId }
        ])

        queryClient.setQueryData<PlatformContentQuery>(
          ['platformContent', { id: accountId }],
          (prev) => {
            if (!prev) return prev

            const { platformContent } = prev
            const hasFolderId = folderId !== null

            const contentsToMove = hasFolderId
              ? platformContent?.customContents?.filter(
                  (content) =>
                    ids.includes(content.id) && content.folderId !== folderId
                ) ?? []
              : platformContent?.folders?.flatMap(
                  (folder) =>
                    folder.customPages?.filter((content) =>
                      ids.includes(content.id)
                    ) ?? []
                ) ?? []

            const updatedFolders = platformContent?.folders?.map((folder) => {
              const isTargetFolder = folderId && folder.id === folderId

              const filteredPages =
                folder.customPages?.filter(
                  (content) => !ids.includes(content.id)
                ) ?? []

              return {
                ...folder,
                customPages:
                  hasFolderId && isTargetFolder
                    ? [
                        ...filteredPages,
                        ...contentsToMove.map((content) => ({
                          ...content,
                          folderId
                        }))
                      ]
                    : filteredPages
              }
            })

            const updatedCustomContents = hasFolderId
              ? platformContent?.customContents?.filter(
                  (content) => !ids.includes(content.id)
                )
              : [
                  ...(platformContent?.customContents ?? []),
                  ...contentsToMove.map((content) => ({
                    ...content,
                    folderId: null
                  }))
                ]

            return {
              platformContent: {
                ...platformContent,
                id: platformContent?.id ?? '',
                folders: updatedFolders,
                customContents: updatedCustomContents
              }
            }
          }
        )

        return { previousData }
      },
      onError: (_, _variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData<PlatformContentQuery>(
            ['platformContent', { id: accountId }],
            context.previousData
          )
        }
      },
      onSettled: async () => {
        await Promise.all([
          queryClient.invalidateQueries(['platformContent', { id: accountId }]),
          queryClient.invalidateQueries(['platformClient', { id: accountId }])
        ])
      }
    }
  )
}
